import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
	Badge,
	Box,
	ClappingHandsOutline,
	EyeOutline,
	HStack,
	IconButton,
	MegaphoneOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	Text,
	useToast,
	VStack,
} from '@bonusly/design-system';
import { DotsHorizontalOutline } from '@bonusly/ds-icon';

import { useSendSpotlight } from '../../../queries/employee-spotlights';
import { EmployeeSpotlight } from '../../../schemas/spotlight';
import { SendSpotlightConfirmationModal } from './send-spotlight-confirmation-modal';
import { openSpotlight } from './spotlight-navigation';
import { formatUserSpotlightDateRange } from './user-spotlight-date-range';

export type SpotlightStatus = 'generating' | 'generated' | 'shared' | 'failed';

type SpotlightListItemVariant = 'default' | 'single';

interface SpotlightListItemProps {
	spotlight: EmployeeSpotlight;
	targetUserName?: string;
	onAddToRecognition?: (spotlight: EmployeeSpotlight) => void;
	/** 'single' removes border, hover, click-to-view, and view menu action (footer handles navigation instead) */
	variant?: SpotlightListItemVariant;
}

const getSpotlightStatus = (spotlight: EmployeeSpotlight): SpotlightStatus => {
	if (spotlight.status === 'pending' || spotlight.status === 'processing') {
		return 'generating';
	}
	if (spotlight.status === 'failed') {
		return 'failed';
	}
	if (spotlight.visibility === 'company') {
		return 'shared';
	}
	return 'generated';
};

const getStatusBadgeProps = (status: SpotlightStatus) => {
	switch (status) {
		case 'generating':
			return { colorScheme: 'gray' as const, variant: 'subtle' as const };
		case 'generated':
			return { bg: 'teal.100', color: 'teal.600' };
		case 'shared':
			return { bg: 'teal.100', color: 'teal.600' };
		case 'failed':
			return { colorScheme: 'red' as const, variant: 'subtle' as const };
	}
};

export const SpotlightListItem: FC<SpotlightListItemProps> = ({ spotlight, targetUserName, onAddToRecognition, variant = 'default' }) => {
	const isSingle = variant === 'single';
	const { t } = useTranslation('spotlight');
	const navigate = useNavigate();
	const toast = useToast();
	const [isSendModalOpen, setIsSendModalOpen] = useState(false);

	const status = getSpotlightStatus(spotlight);
	const badgeProps = getStatusBadgeProps(status);
	const dateRange = formatUserSpotlightDateRange(spotlight.startTime, spotlight.endTime);
	const createdDate = format(spotlight.createdAt, 'MMM d, yyyy');

	// TODO: Add anniversary spotlight type field to backend, then update this logic
	const title = t('generatedSpotlightsModal.spotlightTitle', { name: targetUserName || t('viewer.header.defaultName') });

	const { mutate: sendSpotlight, isLoading: isSending } = useSendSpotlight({
		onSuccess: () => {
			setIsSendModalOpen(false);
			toast({
				title: t('callToAction.persona.manager.sendSpotlightSuccess', { name: targetUserName }),
				status: 'success',
				duration: 5000,
			});
		},
		onError: () => {
			toast({
				title: t('callToAction.persona.manager.sendSpotlightError'),
				status: 'error',
				duration: 5000,
			});
		},
	});

	const handleViewSpotlight = () => {
		openSpotlight(spotlight.id, spotlight.url, navigate, 'team_hub');
	};

	const handleAddToRecognition = () => {
		onAddToRecognition?.(spotlight);
	};

	const handleSendSpotlight = () => {
		setIsSendModalOpen(true);
	};

	const handleConfirmSend = () => {
		sendSpotlight({
			spotlightId: spotlight.id,
			recipientUserIds: [spotlight.userId],
		});
	};

	const isCompleted = spotlight.status === 'completed';
	const canShare = isCompleted && spotlight.visibility !== 'company';

	return (
		<>
			<Box
				p={4}
				{...(!isSingle && {
					borderRadius: 'lg',
					border: '1px solid',
					borderColor: 'border.default',
					_hover: { bg: 'bg.subtle', cursor: isCompleted ? 'pointer' : 'default' },
					onClick: isCompleted ? handleViewSpotlight : undefined,
				})}
				bg="bg.surface"
				data-testid="spotlight-list-item"
			>
				<HStack justify="space-between" align="center" spacing={4}>
					<VStack align="start" spacing={0} flex={1} minW={0}>
						<Text fontWeight="semibold" fontSize="md" noOfLines={1} maxW="100%">
							{title}
						</Text>
						<Text fontSize="sm" color="text.soft" noOfLines={1}>
							{dateRange}
						</Text>
					</VStack>

					<Badge {...badgeProps} fontSize="xs" px={2} py={0.5} flexShrink={0}>
						{t(`generatedSpotlightsModal.status.${status}`)}
					</Badge>

					<Text fontSize="sm" color="text.soft" whiteSpace="nowrap" flexShrink={0}>
						{createdDate}
					</Text>

					<Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
						<Menu>
							<MenuButton
								as={IconButton}
								aria-label={t('generatedSpotlightsModal.actionsAriaLabel')}
								icon={<DotsHorizontalOutline size="sm" />}
								variant="ghost"
								size="sm"
								isDisabled={!isCompleted}
								data-testid="spotlight-actions-menu"
							/>
							<Portal>
								<MenuList bg="bg.surface" zIndex="popover">
									{!isSingle && (
										<MenuItem icon={<EyeOutline size="md" />} onClick={handleViewSpotlight} data-testid="view-spotlight-action">
											{t('generatedSpotlightsModal.actions.viewSpotlight')}
										</MenuItem>
									)}
									<MenuItem icon={<ClappingHandsOutline size="md" />} onClick={handleAddToRecognition} data-testid="add-to-recognition-action">
										{t('generatedSpotlightsModal.actions.addToRecognition')}
									</MenuItem>
									{canShare && (
										<MenuItem icon={<MegaphoneOutline size="md" />} onClick={handleSendSpotlight} data-testid="send-spotlight-action">
											{t('generatedSpotlightsModal.actions.sendSpotlight')}
										</MenuItem>
									)}
								</MenuList>
							</Portal>
						</Menu>
					</Box>
				</HStack>
			</Box>

			<SendSpotlightConfirmationModal
				isOpen={isSendModalOpen}
				onClose={() => setIsSendModalOpen(false)}
				onConfirm={handleConfirmSend}
				isSending={isSending}
				subjectDisplayName={targetUserName || ''}
			/>
		</>
	);
};
